import type { ReactNode } from 'react';
import { ExternalLink, Info } from 'lucide-react';
import type {
  BenchmarkGroup,
  BenchmarkTransparency,
  BenchmarkTransparencySource,
} from '../hooks/useBenchmarks';

export type MarketBucket = 'in_line' | 'below' | 'above';

export interface MarketPosition {
  bucket: MarketBucket;
  deltaPercent: number;
  median: number;
  iqr: number;
}

const formatEuro = (v: number | null | undefined) =>
  v == null || isNaN(v)
    ? '—'
    : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

const BUCKET_COPY: Record<MarketBucket, { label: string; color: string }> = {
  in_line: { label: 'In linea col mercato', color: '#047857' },
  below: { label: 'Sotto la media di mercato', color: '#1d4ed8' },
  above: { label: 'Sopra la media di mercato', color: '#b45309' },
};

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-5">
      <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5"
         style={{ color: 'var(--au-text-3, #9ca3af)' }}>{title}</p>
      {children}
    </div>
  );
}

function Row({ label, value, zebra = false }: { label: string; value: ReactNode; zebra?: boolean }) {
  return (
    <div
      className={`flex items-baseline justify-between gap-3 py-1.5 px-2 rounded-lg`}
      style={zebra ? { backgroundColor: 'var(--au-surface-2, #f8fafc)' } : undefined}
    >
      <span className="text-[13px] shrink-0" style={{ color: 'var(--au-text-2, #6b7280)' }}>{label}</span>
      <span className="text-[13px] font-medium text-right" style={{ color: 'var(--au-text, #111827)' }}>{value}</span>
    </div>
  );
}

interface MarketPriceTransparencyProps {
  group: BenchmarkGroup;
  price: number;
  position: MarketPosition;
  matched?: string | null;
}

export default function MarketPriceTransparency({ group, price, position, matched }: MarketPriceTransparencyProps) {
  const t: BenchmarkTransparency | null = group.transparency;
  const method = typeof t?.method === 'object' && t.method ? t.method : null;
  const sampleTotal = method?.sample_total ?? t?.total_listings;
  const sampleUsed = method?.sample_used ?? t?.used_listings;
  const sources: BenchmarkTransparencySource[] =
    t?.sources && t.sources.length > 0 ? t.sources : t?.by_source ?? [];
  const listings = t?.listings ?? [];
  const bucket = BUCKET_COPY[position.bucket];

  const matchedLabel =
    matched === 'exact'
      ? 'Esatta (marca, modello, anno)'
      : matched === 'fuzzy'
      ? 'Approssimata (stesso modello, anno e lunghezza simili)'
      : null;

  const methodRows: { label: string; value: ReactNode }[] = [
    { label: 'Metodo', value: method?.statistic ?? 'Mediana' },
  ];
  if (method?.outlier_rule) methodRows.push({ label: 'Regola outlier', value: method.outlier_rule });
  methodRows.push({ label: 'Annunci raccolti', value: sampleTotal ?? group.listing_count ?? '—' });
  methodRows.push({ label: 'Annunci usati per la stima', value: sampleUsed ?? group.listing_count ?? '—' });
  if (method?.excluded_outliers != null) {
    methodRows.push({ label: 'Esclusi come outlier', value: String(method.excluded_outliers) });
  }
  if (group.confidence != null) {
    methodRows.push({ label: 'Affidabilità stima', value: `${Math.round(group.confidence)}%` });
  }
  if (matchedLabel) methodRows.push({ label: 'Tipo di corrispondenza', value: matchedLabel });
  const freshnessValue: ReactNode =
    t?.freshness_days != null
      ? `dati di ${t.freshness_days} gg fa`
      : group.freshness_window_days
      ? `finestra di ${group.freshness_window_days} gg`
      : '—';

  const transparencyRows: { label: string; value: ReactNode }[] = [];
  if (group.display_name) transparencyRows.push({ label: 'Gruppo di confronto', value: group.display_name });
  if (group.year_segment) transparencyRows.push({ label: 'Segmento anno', value: String(group.year_segment) });
  if (group.length_segment) transparencyRows.push({ label: 'Segmento lunghezza', value: `${group.length_segment} m` });
  transparencyRows.push({ label: 'Freschezza dati', value: freshnessValue });
  if (formatDate(group.last_updated)) {
    transparencyRows.push({ label: 'Aggiornato il', value: formatDate(group.last_updated) as string });
  }

  return (
    <div className="px-4 pb-8">
      <p className="text-[12px] leading-relaxed mb-4" style={{ color: 'var(--au-text-2, #6b7280)' }}>
        Confrontiamo il prezzo di questo annuncio con la <b>mediana</b> dei prezzi di barche simili (stesso modello,
        anno e lunghezza) raccolti automaticamente dal monitoraggio Scout.
      </p>

      <div className="rounded-xl p-3 mb-5"
           style={{ backgroundColor: 'var(--au-surface-2, #f8fafc)', border: '1px solid var(--au-border, #e2e8f0)' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px]" style={{ color: 'var(--au-text-3, #9ca3af)' }}>Questo annuncio</p>
            <p className="text-lg font-bold" style={{ color: 'var(--au-text, #111827)' }}>{formatEuro(price)}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px]" style={{ color: 'var(--au-text-3, #9ca3af)' }}>Mediana di mercato</p>
            <p className="text-lg font-semibold" style={{ color: 'var(--au-text, #111827)' }}>{formatEuro(position.median)}</p>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ backgroundColor: 'var(--au-surface, #ffffff)', border: `1px solid ${bucket.color}`, color: bucket.color }}
          >
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: bucket.color }} />
            {bucket.label}
          </span>
          <span className="text-sm font-semibold" style={{ color: bucket.color }}>
            {position.deltaPercent >= 0 ? '+' : ''}
            {position.deltaPercent}% {position.deltaPercent >= 0 ? 'sopra' : 'sotto'} la mediana
          </span>
        </div>
      </div>

      <Section title="Come l'abbiamo calcolato">
        {methodRows.map((r, i) => (
          <Row key={r.label} label={r.label} value={r.value} zebra={i % 2 === 1} />
        ))}
      </Section>

      {sources.length > 0 ? (
        <Section title="Fonti raccolte">
          {sources.map((s, i) => (
            <div key={s.source ?? i} className="py-1.5 border-b last:border-0"
                 style={{ borderColor: 'var(--au-border, #C6C6C8)' }}>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold" style={{ color: 'var(--au-text, #111827)' }}>{s.label || s.source}</span>
                <span className="text-[11px]" style={{ color: 'var(--au-text-3, #9ca3af)' }}>
                  {s.count} annuncio{s.count === 1 ? '' : 'i'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px] mt-1" style={{ color: 'var(--au-text-2, #6b7280)' }}>
                <span>Min {formatEuro(s.min_price ?? s.min)}</span>
                <span className="font-medium text-center" style={{ color: 'var(--au-text, #374151)' }}>Mediana {formatEuro(s.median_price ?? s.median)}</span>
                <span className="text-right">Max {formatEuro(s.max_price ?? s.max)}</span>
              </div>
            </div>
          ))}
        </Section>
      ) : null}

      {listings.length > 0 ? (
        <Section title={`Annunci confrontati (${listings.length})`}>
          {listings.map((l, i) => (
            <div key={l.url ?? i} className="flex items-center justify-between gap-2 py-2 border-b last:border-0"
                 style={{ borderColor: 'var(--au-border, #C6C6C8)' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium truncate" style={{ color: 'var(--au-text, #111827)' }}>{l.title || l.shop_name || 'Annuncio'}</p>
                <p className="text-[11px]" style={{ color: 'var(--au-text-2, #6b7280)' }}>
                  {l.shop_name ? <>{l.shop_name} · </> : null}
                  {formatEuro(l.price)}
                  {formatDate(l.listed_at) ? <> · {formatDate(l.listed_at)}</> : null}
                  {l.inlier === false ? ' · escluso come outlier' : null}
                </p>
              </div>
              {l.url ? (
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg text-white"
                  style={{ backgroundColor: 'var(--au-cyan, #1d4ed8)' }}
                >
                  <ExternalLink className="w-3 h-3" />
                  Vedi
                </a>
              ) : null}
            </div>
          ))}
        </Section>
      ) : null}

      <Section title="Trasparenza dati">
        {transparencyRows.map((r, i) => (
          <Row key={r.label} label={r.label} value={r.value} zebra={i % 2 === 1} />
        ))}
      </Section>

      {!t ? (
        <div className="flex items-start gap-2 rounded-xl p-3" style={{ backgroundColor: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)' }}>
          <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#fbbf24' }} />
          <p className="text-[12px] leading-relaxed" style={{ color: '#fbbf24' }}>
            Dettaglio Scout non disponibile: mostriamo i valori del gruppo di confronto senza il dettaglio degli
            annunci raccolti.
          </p>
        </div>
      ) : null}
    </div>
  );
}
